import * as React from 'react';
import type { AccountInfo } from '@azure/msal-browser';
import { Button, Checkbox, Dropdown, Input, MessageBar, MessageBarBody, Option, Spinner } from '@fluentui/react-components';
import { AddRegular, DeleteRegular, DocumentFolderRegular, HistoryRegular, SearchRegular, TextBulletListLtrRegular } from '@fluentui/react-icons';
import { GraphService, type IGraphClient, type ISiteInfo } from '../../../src/services/GraphService';
import type { ILibraryInfo } from '../../../src/models/LibraryInfo';
import { LIST_TITLE } from '../../../src/services/auditLogSchema';
import { newWorkspace, type IStaff, type IWorkspace } from '../../api/src/workspace';
import { FAMILY_LABELS, PROGRAMS, type IProgram, type ProgramFamily } from '../../api/src/programs';
import { saveWorkspace, type IWorkspaceSummary } from '../api';
import { errorText, useApp } from '../app/context';
import { siteUrlOf } from '../app/App';
import { PRODUCT_NAME } from '../app/product';
import { getToken, resumeAfterSignIn, takeResume } from '../auth';
import { MANAGE_SCOPES } from '../config';
import { ALL_LISTS } from '../records/schemas';
import { ensureFolders } from '../records/folders';
import { CLIENTS_FOLDER } from '../records/paths';

/** What the person chose; nothing is created in SharePoint until step 4. */
interface IChoices {
  site?: ISiteInfo;
  library?: ILibraryInfo;
  staff: IStaff[];
  programs: string[];
}

const STEPS: string[] = ['Your site', 'Your team', 'Programs', 'Check and create'];

/**
 * A create that was asked for before the library was connected: carried over while the app loads
 * the new library, then finished here (same page load; a Microsoft redirect uses takeResume instead).
 */
let pendingCreate: { driveId: string; staff: IStaff[]; programs: string[] } | undefined;

/**
 * Onboarding before any library is connected (or when connecting another one). Choosing happens
 * here; "Create and start" connects the library, and the app carries on in OnboardingInApp.
 */
export function OnboardingStart(props: {
  graph: IGraphClient;
  account: AccountInfo;
  connected: IWorkspaceSummary[];
  onConnected: (w: IWorkspace) => Promise<void>;
  onCancel?: () => void;
}): React.ReactElement {
  const me: IStaff = { name: props.account.name || props.account.username, email: props.account.username, role: 'lawyer' };
  return (
    <Steps
      graph={props.graph}
      account={props.account}
      initial={{ staff: [me], programs: PROGRAMS.map(p => p.id) }}
      onCancel={props.onCancel}
      create={async c => {
        const w: IWorkspace = await connect(c, props.connected);
        pendingCreate = { driveId: w.driveId, staff: c.staff, programs: c.programs };
        await props.onConnected(w);
      }}
    />
  );
}

/** Saves the chosen library's settings (our table only; SharePoint is untouched until the lists are created). */
async function connect(c: IChoices, connected: IWorkspaceSummary[]): Promise<IWorkspace> {
  const existing: IWorkspaceSummary | undefined = connected.filter(w => w.driveId === c.library!.id)[0];
  if (existing) {
    return { ...newWorkspace(c.library!.id), setupDone: existing.setupDone };
  }
  return saveWorkspace({
    ...newWorkspace(c.library!.id), siteId: c.site!.id, siteName: c.site!.displayName, libraryName: c.library!.name, webUrl: c.library!.webUrl
  });
}

/** Onboarding for a connected library that isn't set up yet (or "Run setup again" from Settings). */
export function OnboardingInApp(): React.ReactElement {
  const app = useApp();
  const w = app.workspace;
  const me: IStaff = { name: app.account.name || app.account.username, email: app.account.username, role: 'lawyer' };
  const [working, setWorking] = React.useState<string | undefined>(undefined);
  const [error, setError] = React.useState<string | undefined>(undefined);

  const setUp = React.useCallback(async (people: IStaff[], chosen: string[]): Promise<void> => {
    setError(undefined);
    setWorking('Asking Microsoft for permission to create lists in your site…');
    try {
      // Creating lists needs the site-management permission: asked from this click (a page redirect,
      // never a popup), and the setup carries on by itself when the person comes back.
      resumeAfterSignIn({ kind: 'firmSetup', staff: people, programs: chosen });
      await getToken(MANAGE_SCOPES, true, true);
      await app.records.ensureAll(title => setWorking(`Creating the ${title} list…`));
      setWorking(`Creating the ${CLIENTS_FOLDER} folder…`);
      await ensureFolders(app.manageGraph, w, app.audit, app.account.username, [CLIENTS_FOLDER]);
      setWorking('Saving…');
      await app.saveWorkspace({
        ...w,
        packId: 'on-law-immigration',
        paused: w.setupDone ? w.paused : true, // Inbox filing stays off until the firm turns it on.
        setupDone: true,
        firm: { ...w.firm, staff: people, programs: chosen.length === PROGRAMS.length ? [] : chosen, listsReady: new Date().toISOString() }
      });
      app.go('home');
    } catch (e) {
      setError(errorText(e));
      setWorking(undefined);
    }
  }, [app, w]);

  // Carried over from connecting the library, or back from Microsoft: finish what was started.
  React.useEffect(() => {
    // Only for this library: a create carried over for another one waits until that one is open.
    const mine = pendingCreate?.driveId === w.driveId ? pendingCreate : undefined;
    const r = mine ? undefined : takeResume('firmSetup');
    const carried = mine ?? (r && Array.isArray(r.staff) && Array.isArray(r.programs) ? { staff: r.staff as IStaff[], programs: r.programs as string[] } : undefined);
    if (mine) {
      pendingCreate = undefined;
    }
    if (carried) {
      void setUp(carried.staff, carried.programs);
    }
    // Once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initial: IChoices = {
    site: w.siteId ? { id: w.siteId, displayName: w.siteName, webUrl: siteUrlOf(w.webUrl) } : undefined,
    library: { id: w.driveId, name: w.libraryName, webUrl: w.webUrl },
    staff: w.firm?.staff.length ? w.firm.staff : [me],
    programs: w.firm?.programs.length ? w.firm.programs : PROGRAMS.map(p => p.id)
  };

  return (
    <Steps
      graph={app.graph}
      account={app.account}
      initial={initial}
      startAt={working ? 3 : initial.site ? 1 : 0}
      working={working}
      error={error}
      onCancel={working ? undefined : () => app.go('home')}
      create={async c => {
        if (c.library!.id !== w.driveId) {
          // Another site or library than the one connected: connect that one, then carry on there.
          const next: IWorkspace = await connect(c, app.workspaces);
          pendingCreate = { driveId: next.driveId, staff: c.staff, programs: c.programs };
          app.switchLibrary(next.driveId);
          return;
        }
        await setUp(c.staff, c.programs);
      }}
    />
  );
}

function Steps(props: {
  graph: IGraphClient;
  account: AccountInfo;
  initial: IChoices;
  startAt?: number;
  working?: string;
  error?: string;
  onCancel?: () => void;
  create: (c: IChoices) => Promise<void>;
}): React.ReactElement {
  const [step, setStep] = React.useState<number>(props.startAt ?? 0);
  const [choices, setChoices] = React.useState<IChoices>(props.initial);
  const [busy, setBusy] = React.useState<string | undefined>(undefined);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const working: string | undefined = props.working ?? busy;
  const problem: string | undefined = props.error ?? error;
  React.useEffect(() => { if (props.working) { setStep(3); } }, [props.working]);
  React.useEffect(() => { window.scrollTo?.(0, 0); }, [step]);

  const named: IStaff[] = choices.staff.filter(s => s.name.trim());
  const ready: boolean[] = [!!choices.site && !!choices.library, named.some(s => s.role === 'lawyer'), choices.programs.length > 0, true];

  const create = async (): Promise<void> => {
    setBusy('Connecting the library…');
    setError(undefined);
    try {
      await props.create({ ...choices, staff: named });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(undefined);
    }
  };

  return (
    <div className="onb">
      <nav aria-label="Setup steps">
        <ol className="onb-steps">
          {STEPS.map((label, i) => (
            <li key={label} aria-current={i === step ? 'step' : undefined} className={i < step ? 'done' : undefined}>
              <span className="dot" aria-hidden="true">{i < step ? '✓' : i + 1}</span>{label}
            </li>
          ))}
          {props.onCancel && <li style={{ marginLeft: 'auto' }}><Button appearance="subtle" onClick={props.onCancel}>Cancel</Button></li>}
        </ol>
      </nav>
      <main className="onb-main">
        <div className="onb-grid">
          <section>
            <div className="stack" style={{ gap: 8 }}>
              <span className="onb-kicker">Step {step + 1} of 4</span>
              <h1 className="h1">{['Where should client files live?', 'Who works on files?', 'Which programs does your firm handle?', 'Here\'s what we\'ll create'][step]}</h1>
              <p className="onb-lead">{[
                'Pick the SharePoint site your firm uses for client work. Client records and documents stay there, in your firm\'s Microsoft 365.',
                'Each file gets a lawyer and, if you like, a clerk. A lawyer signs off before anything is submitted, so add at least one.',
                'Only these show when someone opens an application. Each comes with a checklist built from IRCC\'s own pages.',
                <>All of it goes into <strong>{choices.site?.displayName}</strong>. Nothing already there is changed, and the folder can be undone from History.</>
              ][step]}</p>
            </div>
            {problem && <MessageBar intent="error"><MessageBarBody>{problem}</MessageBarBody></MessageBar>}
            {step === 0 && <SiteStep graph={props.graph} choices={choices} onChange={c => setChoices(x => ({ ...x, ...c }))} />}
            {step === 1 && <TeamStep staff={choices.staff} onChange={staff => setChoices(x => ({ ...x, staff }))} />}
            {step === 2 && <ProgramsStep programs={choices.programs} onChange={programs => setChoices(x => ({ ...x, programs }))} />}
            {step === 3 && <CreateStep choices={choices} onChange={setStep} />}
            {step === 3 && <p className="small" style={{ margin: 0, fontSize: 14 }}>Microsoft may ask you to allow {PRODUCT_NAME} to create lists in your site. That page comes back here when you're done.</p>}
            <div className="onb-nav">
              {step > 0
                ? <Button size="large" disabled={!!working} onClick={() => setStep(step - 1)}>Back</Button>
                : props.onCancel ? <Button size="large" onClick={props.onCancel}>Back</Button> : <span />}
              {step < 3 && <Button size="large" appearance="primary" disabled={!ready[step]} onClick={() => setStep(step + 1)}>Continue</Button>}
              {step === 3 && (working
                ? <Spinner size="small" label={working} labelPosition="after" />
                : <Button size="large" appearance="primary" disabled={!ready.every(Boolean)} onClick={() => { void create(); }}>Create and start</Button>)}
            </div>
          </section>
          <aside>
            {step === 0 && (
              <>
                <div className="card">
                  <strong>Good to know</strong>
                  <p>Nothing is created yet. On the last step you'll see every list and folder before you confirm.</p>
                  <p>You need to be an owner of the site. If you're not, ask your Microsoft 365 admin, or pick another site.</p>
                  <p>Not sure? Most firms pick the site their client folders are already in.</p>
                </div>
                <p className="small">About 5 minutes in all.</p>
              </>
            )}
            {step === 1 && (
              <>
                <div className="card">
                  <strong>Lawyers and clerks</strong>
                  <p><strong>Lawyers</strong> are assigned files and sign off before submission.</p>
                  <p><strong>Clerks</strong> collect documents, record IRCC letters and prepare the package.</p>
                  <p>Everyone with access to the site can use the app. This list only decides who files are assigned to.</p>
                </div>
                <p className="small">You can change this later in Settings.</p>
              </>
            )}
            {step === 2 && (
              <>
                <div className="card">
                  <strong>About the checklists</strong>
                  <p>Each one follows IRCC's pages as of a date it shows, and links to them. IRCC's personalized checklist from the portal always comes first.</p>
                  <p>Until a lawyer has reviewed a checklist, it's marked <strong>pending review</strong> wherever it appears.</p>
                </div>
                <div className="card">
                  <strong>Restricted files</strong>
                  <p>Refugee and private-sponsorship files are kept in their own library, open only to a group you choose. A site owner sets this up after setup, in Settings.</p>
                </div>
              </>
            )}
            {step === 3 && (
              <>
                <div className="card">
                  <strong>After this</strong>
                  <p>Today shows a short getting-started list: add a client, open an application, and, when you're ready, turn on restricted files and client upload links.</p>
                  <p>Have clients in a spreadsheet? You can import them from a CSV file.</p>
                </div>
                <div className="card">
                  <strong>If it stops halfway</strong>
                  <p>Run setup again from Settings. It picks up where it left off and won't make duplicates.</p>
                </div>
              </>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
}

function SiteStep(props: { graph: IGraphClient; choices: IChoices; onChange: (c: Partial<IChoices>) => void }): React.ReactElement {
  const service = React.useMemo(() => new GraphService(props.graph), [props.graph]);
  const [query, setQuery] = React.useState<string>('');
  const [sites, setSites] = React.useState<ISiteInfo[] | undefined>(undefined);
  const [libraries, setLibraries] = React.useState<ILibraryInfo[] | undefined>(undefined);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const site: ISiteInfo | undefined = props.choices.site;

  const search = React.useCallback((text: string) => {
    setSites(undefined);
    setError(undefined);
    service.searchSites(text).then(setSites).catch(e => setError(errorText(e)));
  }, [service]);
  React.useEffect(() => { search(''); }, [search]);

  // The chosen site's libraries: "Documents" (the site's own) is picked unless the person picks another.
  React.useEffect(() => {
    if (!site) {
      setLibraries(undefined);
      return;
    }
    let live: boolean = true;
    service.getLibraries(site.id).then(libs => {
      if (!live) {
        return;
      }
      setLibraries(libs);
      if (!props.choices.library || !libs.some(l => l.id === props.choices.library!.id)) {
        const pick: ILibraryInfo | undefined = libs.filter(l => /\/Shared(%20| )Documents$/i.test(l.webUrl))[0] ?? libs[0];
        props.onChange({ library: pick });
      }
    }).catch(e => setError(errorText(e)));
    return () => { live = false; };
    // Only when the site changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site?.id, service]);

  const shown: ISiteInfo[] = site && sites && !sites.some(s => s.id === site.id) ? [site, ...sites] : sites ?? [];
  return (
    <>
      <form onSubmit={e => { e.preventDefault(); search(query); }} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <label htmlFor="site-search" style={{ fontWeight: 600, fontSize: 14 }}>Search your sites</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <Input id="site-search" size="large" placeholder="Site name, e.g. Clients" value={query} onChange={(_, d) => setQuery(d.value)} contentBefore={<SearchRegular />} style={{ flexGrow: 1 }} />
          <Button size="large" type="submit">Search</Button>
        </div>
      </form>
      {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      {!sites && !error && <Spinner size="small" label="Finding sites…" labelPosition="after" />}
      {sites && shown.length === 0 && <p className="muted" style={{ margin: 0 }}>No sites found. Try another name.</p>}
      <div role="radiogroup" aria-label="Sites" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {shown.slice(0, 12).map(s => {
          const on: boolean = site?.id === s.id;
          return (
            <div key={s.id} className="option" role="radio" aria-checked={on} tabIndex={0}
              onClick={() => { if (!on) { props.onChange({ site: s, library: undefined }); } }}
              onKeyDown={e => { if ((e.key === ' ' || e.key === 'Enter') && !on) { e.preventDefault(); props.onChange({ site: s, library: undefined }); } }}>
              <span className="radio" aria-hidden="true" />
              <span className="stack" style={{ flex: 1 }}>
                <strong style={{ fontSize: 16 }}>{s.displayName}</strong>
                <span className="small" style={{ fontSize: 14 }}>{s.webUrl.replace(/^https:\/\//, '')}</span>
                {on && !libraries && <Spinner size="tiny" label="Finding its libraries…" labelPosition="after" />}
                {on && libraries && libraries.length === 1 && <span style={{ fontSize: 14 }}>Library: <strong>{libraries[0].name}</strong></span>}
                {on && libraries && libraries.length > 1 && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                    Library:
                    <Dropdown aria-label="Library" value={props.choices.library?.name ?? ''} selectedOptions={props.choices.library ? [props.choices.library.id] : []}
                      onOptionSelect={(_, d) => props.onChange({ library: libraries.filter(l => l.id === d.optionValue)[0] })} style={{ minWidth: 200 }}>
                      {libraries.map(l => <Option key={l.id} value={l.id}>{l.name}</Option>)}
                    </Dropdown>
                  </span>
                )}
                {on && libraries && libraries.length === 0 && <span className="small" style={{ color: 'var(--red)' }}>This site has no document library. Pick another site.</span>}
              </span>
            </div>
          );
        })}
      </div>
    </>
  );
}

function TeamStep(props: { staff: IStaff[]; onChange: (s: IStaff[]) => void }): React.ReactElement {
  const update = (i: number, change: Partial<IStaff>): void => props.onChange(props.staff.map((s, j) => (j === i ? { ...s, ...change } : s)));
  const named: IStaff[] = props.staff.filter(s => s.name.trim());
  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {props.staff.map((s, i) => (
          <div key={i} className="card" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr)) auto', gap: 12, alignItems: 'end', padding: 16 }}>
            <label className="stack" style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-3)' }}>Name<Input value={s.name} onChange={(_, d) => update(i, { name: d.value })} /></label>
            <label className="stack" style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-3)' }}>Work email<Input type="email" value={s.email ?? ''} onChange={(_, d) => update(i, { email: d.value || undefined })} /></label>
            <label className="stack" style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-3)' }}>Role
              <Dropdown value={s.role === 'lawyer' ? 'Lawyer' : 'Clerk'} selectedOptions={[s.role]} onOptionSelect={(_, d) => update(i, { role: d.optionValue === 'lawyer' ? 'lawyer' : 'clerk' })} style={{ minWidth: 0 }}>
                <Option value="lawyer">Lawyer</Option>
                <Option value="clerk">Clerk</Option>
              </Dropdown>
            </label>
            <Button appearance="subtle" icon={<DeleteRegular />} aria-label={`Remove ${s.name || 'this person'}`} disabled={props.staff.length === 1} onClick={() => props.onChange(props.staff.filter((_, j) => j !== i))} />
          </div>
        ))}
        <Button icon={<AddRegular />} onClick={() => props.onChange([...props.staff, { name: '', role: 'clerk' }])} style={{ alignSelf: 'flex-start' }}>Add a person</Button>
      </div>
      {!named.some(s => s.role === 'lawyer') && <span className="needs-you" style={{ fontSize: 14 }}>Add at least one lawyer.</span>}
      <p className="small" style={{ margin: 0, fontSize: 14 }}>A lawyer's sign-off button only works for the person whose email matches here, so use the email they sign in with.</p>
    </>
  );
}

export function ProgramsStep(props: { programs: string[]; onChange: (p: string[]) => void }): React.ReactElement {
  const families: ProgramFamily[] = (Object.keys(FAMILY_LABELS) as ProgramFamily[]).filter(f => PROGRAMS.some(p => p.family === f));
  const [open, setOpen] = React.useState<string[]>([]);
  const has = (id: string): boolean => props.programs.indexOf(id) !== -1;
  return (
    <>
      <div className="spread">
        <strong>{props.programs.length} of {PROGRAMS.length} programs chosen</strong>
        <span style={{ display: 'flex', gap: 16, fontSize: 14 }}>
          <a href="#" onClick={e => { e.preventDefault(); props.onChange(PROGRAMS.map(p => p.id)); }}>Choose all</a>
          <a href="#" onClick={e => { e.preventDefault(); props.onChange([]); }}>Clear</a>
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {families.map(f => {
          const list: IProgram[] = PROGRAMS.filter(p => p.family === f);
          const n: number = list.filter(p => has(p.id)).length;
          const expanded: boolean = open.indexOf(f) !== -1;
          const restricted: boolean = list.some(p => p.restricted);
          return (
            <div key={f} className="grp">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Checkbox
                  checked={n === list.length ? true : n ? 'mixed' : false}
                  onChange={(_, d) => props.onChange(d.checked ? [...props.programs.filter(id => !list.some(p => p.id === id)), ...list.map(p => p.id)] : props.programs.filter(id => !list.some(p => p.id === id)))}
                  label={<strong>{FAMILY_LABELS[f]}</strong>}
                />
                {restricted && <span className="tag-warn" style={{ marginLeft: 0 }}>Restricted</span>}
                <span className="small" style={{ marginLeft: 'auto' }}>{n} of {list.length}</span>
                <Button appearance="transparent" size="small" aria-expanded={expanded} onClick={() => setOpen(o => (expanded ? o.filter(x => x !== f) : [...o, f]))}>{expanded ? 'Hide' : 'Choose'}</Button>
              </div>
              {expanded && list.map(p => (
                <Checkbox key={p.id} style={{ paddingLeft: 24 }} checked={has(p.id)}
                  onChange={(_, d) => props.onChange(d.checked ? [...props.programs, p.id] : props.programs.filter(x => x !== p.id))}
                  label={<span>{p.name}{p.status === 'paused' ? <span className="small"> · intake paused by IRCC</span> : null}{p.review === 'pending' ? <span className="small"> · pending review</span> : null}</span>} />
              ))}
            </div>
          );
        })}
      </div>
    </>
  );
}

function CreateStep(props: { choices: IChoices; onChange: (step: number) => void }): React.ReactElement {
  const c: IChoices = props.choices;
  const lawyers: number = c.staff.filter(s => s.name.trim() && s.role === 'lawyer').length;
  const clerks: number = c.staff.filter(s => s.name.trim() && s.role === 'clerk').length;
  const change = (step: number): React.ReactElement => <a href="#" onClick={e => { e.preventDefault(); props.onChange(step); }}>Change</a>;
  return (
    <>
      <div className="card" style={{ gap: 0, padding: '4px 22px' }}>
        <div className="create-row">
          <span className="kind" aria-hidden="true"><DocumentFolderRegular fontSize={20} /></span>
          <div className="stack"><strong>A folder called {CLIENTS_FOLDER}</strong><span className="small" style={{ fontSize: 14 }}>In the {c.library?.name} library. Each client gets a folder inside, then one per application and per person.</span></div>
        </div>
        <div className="create-row">
          <span className="kind" aria-hidden="true"><TextBulletListLtrRegular fontSize={20} /></span>
          <div className="stack">
            <strong>{ALL_LISTS.length} lists for your records</strong>
            <span className="small" style={{ fontSize: 14 }}>{ALL_LISTS.map(l => l.title).join(' · ')}</span>
            <span className="small" style={{ fontSize: 14 }}>They're ordinary SharePoint lists. Your records stay readable even without this app.</span>
          </div>
        </div>
        <div className="create-row">
          <span className="kind" aria-hidden="true"><HistoryRegular fontSize={20} /></span>
          <div className="stack"><strong>A History list</strong><span className="small" style={{ fontSize: 14 }}>"{LIST_TITLE}": every change the app makes is written here, with who and when, so it can be undone.</span></div>
        </div>
      </div>
      <div>
        <strong>Your choices</strong>
        <div className="sum" style={{ marginTop: 8 }}><span className="muted">Site</span><span>{c.site?.displayName} · {c.library?.name} · {change(0)}</span></div>
        <div className="sum"><span className="muted">Team</span><span>{lawyers} lawyer{lawyers === 1 ? '' : 's'}, {clerks} clerk{clerks === 1 ? '' : 's'} · {change(1)}</span></div>
        <div className="sum"><span className="muted">Programs</span><span>{c.programs.length} of {PROGRAMS.length} · {change(2)}</span></div>
      </div>
    </>
  );
}
